import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Calendar as CalendarIcon, User } from 'lucide-react';
import { supabase } from '../../../shared/lib/supabase';
import { useAuth } from '../../../features/auth/contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { TeamSelector } from '../../../features/teams/components/TeamSelector';
import { TeamProjectNavigation } from '../../../features/teams/components/TeamProjectNavigation';


export const TopBar: React.FC = () => {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Check if we're on a project page
  const isProjectPage = location.pathname.startsWith('/projects/');

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserDropdown]);

  return (
    <div className="fixed top-0 left-0 right-0 bg-white z-50 shadow-sm">
      <div className="px-5">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Logo and Team Selector */}
          <div className="flex items-center gap-6">
            {/* Logo */}
            <Link to="/" className="flex items-center hover:opacity-80 transition-all duration-200">
              <span className="text-2xl font-semibold text-black tracking-tight">
                anthrogen:drift
              </span>
            </Link>

            {/* Team/Project Navigation */}
            {user && (isProjectPage ? <TeamProjectNavigation /> : <TeamSelector />)}
          </div>

          {/* Right side - Calendar and User */}
          <div className="flex items-center space-x-3">
            <Link
              to="/calendar"
              className="p-3 hover:bg-gray-100 rounded-sm transition-all duration-200 group"
              title="Calendar"
            >
              <CalendarIcon className="w-5 h-5 text-gray-600 group-hover:text-gray-900 group-hover:scale-110 transition-all" />
            </Link>
            
            {user && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 rounded-sm transition-all duration-200"
                >
                  <span className="text-base font-medium text-gray-700">{user.email}</span>
                  {profile?.avatar_url ? (
                    <img 
                      src={profile.avatar_url} 
                      alt="Profile"
                      className="w-7 h-7 rounded-full object-cover"
                      onError={(e) => {
                        // If image fails to load, hide it and show fallback
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.nextElementSibling;
                        if (fallback) fallback.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-medium text-sm ${profile?.avatar_url ? 'hidden' : ''}`}>
                    {user.email?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </button>

                {/* Dropdown menu */}
                {showUserDropdown && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-sm shadow-lg py-1">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">{user.email?.split('@')[0]}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    
                    <button
                      onClick={() => {
                        navigate('/settings');
                        setShowUserDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                    >
                      <User className="w-4 h-4" />
                      Profile Settings
                    </button>

                    <button
                      onClick={() => {
                        handleSignOut();
                        setShowUserDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                    >
                      <LogOut className="w-4 h-4" />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};